import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { parse, stringify } from 'qs';
import axios from 'axios';

import {
  Sidebar,
  InboxTable,
  PageLoading,
  ErrorDisplay,
} from '../../components';
import ReplyMessageSidebar from '../../components/inbox/replyMessageSidebar';
import SmsMessageSidebar from '../../components/inbox/smsMessageSidebar';
import ReferralReplySidebar from '../../components/inbox/referralReplySidebar';
import {
  checkTriggered,
  changeSidebarDate,
  changeSidebarMenu,
} from '../../actions/sidebar';
import { changeInboxPage } from '../../actions/inbox';
import {
  clearCurrentRecordId,
  markRecordAsViewed,
  clearSelectedRecordDetails,
  clearDownloadUrl,
  clearDocumentComments,
  setSelectedRecordDetails,
} from '../../actions/records';
import {
  initializePreloadCache,
  clearPreloadCache,
} from '../../actions/preload'; // Plain functions, not thunks
import { getRecordResponseById, getAttachmentUrl } from '../../actions/replies';
import { rowSelected } from '../../actions/inboxTableActions';
import { getLabelsByQuery } from '../../actions/labels';
import { getLoginInfo } from '../../actions/auth';
import { changeDefaultOrganization } from '../../actions/appActions';
import { setViewerChatVisible } from '../../actions/aiChat';
import { shouldEnablePreloading } from '../../utils/mobileDetect';
import { loadNewRecordViewer, scheduleAuthenticatedViewerPrefetch } from '../../utils/viewerImports';
import ViewerLoadBoundary from '../../components/viewer/ViewerLoadBoundary';

import '../../components/main.scss';
import DynamicEhrDrawer from '../modals/DynamicEhrDrawer';

const NewRecordViewer = React.lazy(loadNewRecordViewer);

const parseOptions = {
  ignoreQueryPrefix: true,
};

const stringifyOptions = {
  format: 'RFC1738',
  addQueryPrefix: true,
  arrayFormat: 'brackets',
  encode: true,
  sort: (a, b) => (a.localeCompare(b)),
};

const RecordsHOC = (PassedProps) => (PassedComponent) => {
  class Records extends Component {
    static ASYNC_COUNT_RETRY_DELAY_MS = 2000;

    // Debounce window for background refreshes so bursts of
    // REQUEST_INBOX_TABLE_REFRESH bumps (e.g. bulk flows dispatching more than
    // one signal) coalesce into a single fetch.
    static SILENT_REFRESH_DEBOUNCE_MS = 500;

    // Modes whose getPageDataWithQuery action supports the { silent: true } option.
    static SILENT_REFRESH_MODES = ['Received', 'Referred', 'Uploaded', 'Assigned'];

    static propTypes = {
      docked: PropTypes.bool.isRequired,
      loading: PropTypes.bool.isRequired,
      currentRecordLoading: PropTypes.bool,
      errorMessage: PropTypes.string,
      selectedData: PropTypes.array.isRequired,
      departments: PropTypes.array.isRequired,

      columnTitles: PropTypes.arrayOf(PropTypes.object).isRequired,

      headers: PropTypes.shape({
        page: PropTypes.string,
        'per-page': PropTypes.string,
        total: PropTypes.string,
        'total-pages': PropTypes.string,
        'has-more': PropTypes.string,
        'count-state': PropTypes.string,
      }),

      data: PropTypes.arrayOf(PropTypes.shape({
        file_token: PropTypes.string,
        patient_name: PropTypes.string,
        patient_dob: PropTypes.string,
      })).isRequired,

      // Bumped by REQUEST_INBOX_TABLE_REFRESH after record actions (archive,
      // notify, send-to-chart); each bump triggers a silent background refetch.
      inboxRefreshNonce: PropTypes.number,

      location: PropTypes.shape({
        search: PropTypes.string,
        pathname: PropTypes.string,
      }).isRequired,

      // React Router history; used to seed the sticky team-view value into the URL
      // on mount (resolveInitialSearch) and to push filter/pagination changes.
      history: PropTypes.shape({
        push: PropTypes.func,
        replace: PropTypes.func,
      }).isRequired,

      // Static redux actions (same for each parent using this HOC)
      checkTriggered: PropTypes.func.isRequired,
      changeSidebarDate: PropTypes.func.isRequired,
      changeSidebarMenu: PropTypes.func.isRequired,
      rowSelected: PropTypes.func.isRequired,

      // Dynamic redux actions
      // The parent component passes in the function in their PassedProps
      getPageDataWithQuery: PropTypes.func.isRequired,
      getSelectedPageData: PropTypes.func.isRequired,

      revokeToggle: PropTypes.func,
      organizationDetails: PropTypes.shape({
        organizations: PropTypes.array.isRequired,
        current_organization: PropTypes.string,
      }).isRequired,
      changeDefaultOrganization: PropTypes.func.isRequired,
      getLoginInfo: PropTypes.func.isRequired,

      // ENG-2514: optional context forwarded to PassedProps.transformRows /
      // transformColumnTitles. Page wrappers that need to drive HOC-side row /
      // column derivation (e.g. referral linked-row grouping) hoist their state
      // above the HOC and pass it through these props.
      expandedReferralEntitySlugs: PropTypes.instanceOf(Set),
      onToggleReferralEntity: PropTypes.func,
      // Teams v3: forwarded to the PassedComponent so its bulk-action toolbar can
      // render the Team selector (see ReferralActionBar).
      teamsEnabled: PropTypes.bool,
      teams: PropTypes.array,
      onSetRecordTeamBulk: PropTypes.func,
    };

    static defaultProps = {
      headers: {
        page: '1',
        'per-page': '10',
        total: '1',
        'total-pages': '1',
        'has-more': 'false',
        'count-state': 'exact',
      },
      errorMessage: null,
      currentRecordLoading: false,
      revokeToggle: () => {},
      expandedReferralEntitySlugs: undefined,
      onToggleReferralEntity: undefined,
      teamsEnabled: false,
      teams: [],
      onSetRecordTeamBulk: () => {},
      inboxRefreshNonce: 0,
    };

    constructor(props) {
      super(props);
      this.getPageDataWithQueryWithAsyncCount = this.getPageDataWithQueryWithAsyncCount.bind(this);
      const { organizationDetails: { current_organization: currentOrganization } } = this.props;
      this.state = {
        mode: PassedProps.mode,
        viewReplySidebar: false,
        replyRow: null,
        viewSmsSidebar: false,
        viewReferralReplySidebar: false,
        sidebarActions: {
          checkTriggered: props.checkTriggered,
          changeSidebarDate: props.changeSidebarDate,
          changeSidebarMenu: props.changeSidebarMenu,
        },
        inboxActions: {
          changeInboxPage,
          getRecordsByPage: props.getSelectedPageData,
          getRecords: this.getPageDataWithQueryWithAsyncCount,
          rowSelected: props.rowSelected,
        },
        openOrganizationFilterMenu: false,
        currentOrganization,
        currentRecordIndex: null,
        isNavigationLoading: false,
        ehrDrawerVisible: false,
        ehrDrawerData: null,
        asyncCountMetadata: null,
        asyncCountMetadataKey: null,
        currentRecordToken: null,
      };
      // Track whether preload cache has been initialized since mount (ENG-2465)
      this.preloadCacheInitialized = false;
      this.asyncCountAbortController = null;
      this.asyncCountResolvedKey = null;
      this.asyncCountPendingKey = null;
      this.asyncCountRetryTimeout = null;
      // Background refresh (REQUEST_INBOX_TABLE_REFRESH) bookkeeping
      this.silentRefreshTimer = null;
      this.silentRefreshAbortController = null;
      this.silentRefreshPending = false;
    }

    handleOpenEhrDrawer = (drawerData) => {
      this.setState({
        ehrDrawerVisible: true,
        ehrDrawerData: drawerData,
      });
    };

    handleCloseEhrDrawer = () => {
      this.setState({
        ehrDrawerVisible: false,
        ehrDrawerData: null,
      });
    };

    // ENG-2514: synchronous derivation of table rows / column titles via PassedProps
    // transforms keeps the first render correct (no flicker on mount). The transforms
    // are pure functions configured per-page; runtime context (e.g. expanded referral
    // slugs) flows through `this.props` so static transforms can read live state.
    getTableRows = () => {
      const { data } = this.props;
      if (typeof PassedProps.transformRows === 'function') {
        return PassedProps.transformRows(data, this.props) || data;
      }
      return data;
    };

    getTableColumnTitles = () => {
      const { columnTitles } = this.props;
      if (typeof PassedProps.transformColumnTitles === 'function') {
        return PassedProps.transformColumnTitles(columnTitles, this.props) || columnTitles;
      }
      return columnTitles;
    };

    componentDidMount() {
      // Reset preload cache initialization flag on mount (ENG-2465)
      this.preloadCacheInitialized = false;
      this.getQueryData(this.props);
      localStorage.removeItem('send_token');
      localStorage.removeItem('from_page');
    }

    componentDidUpdate(prevProps, prevState) {
      // A record action (archive / notify / send-to-chart) asked the inbox to
      // silently refetch its current query in the background.
      if (prevProps.inboxRefreshNonce !== this.props.inboxRefreshNonce) {
        this.scheduleSilentRefresh();
      }

      // A refresh that arrived while the slideout viewer was open is deferred
      // (see fireSilentRefresh); run it once the viewer closes.
      if (
        prevState?.newRecordViewerOpen
        && !this.state.newRecordViewerOpen
        && this.silentRefreshPending
      ) {
        this.scheduleSilentRefresh();
      }

      // Likewise for a refresh deferred behind an in-flight user fetch: fire it
      // once loading flips false, however long that fetch took. (Some org
      // queries run for many seconds; a retry cap here would drop the
      // reconciliation fetch entirely.)
      if (prevProps.loading && !this.props.loading && this.silentRefreshPending) {
        this.scheduleSilentRefresh();
      }

      // Close EHR drawer if user selects a different record
      const prevRecordId = prevProps.selectedData?.[0]?.file_token;
      const currentRecordId = this.props.selectedData?.[0]?.file_token;
      if (
        this.state.ehrDrawerVisible &&
        prevRecordId !== currentRecordId
      ) {
        this.handleCloseEhrDrawer();
      }

      // Initialize preload cache when data changes (ENG-2465)
      const { data } = this.props;
      const prevData = prevProps.data;

      // Short-circuit: skip token comparison when the array reference is identical.
      // Redux reducers use .map() for updates like MARK_RECORD_AS_VIEWED, creating
      // new array references without changing which records are present —
      // only then do we need the deeper file_token comparison.
      if (data !== prevData) {
        const getTokens = (records) => (records || []).map((r) => r.file_token).sort().join(',');
        if (getTokens(data) !== getTokens(prevData)) {
          this.preloadCacheInitialized = false;
        }
      }

      // Initialize cache if we haven't yet for current data set, or if records changed
      // This handles the unmount-remount case where cache was cleared but same records returned
      if (data && !this.preloadCacheInitialized && data.length > 0) {
        // Clear stale cache entries before initializing with new data
        clearPreloadCache('data_refresh');

        // Check if any records have signed_url (feature flag enabled)
        const recordsWithUrls = data.filter((r) => r.signed_url);
        if (recordsWithUrls.length > 0) {
          initializePreloadCache(recordsWithUrls);
        }
        this.preloadCacheInitialized = true;
      }

      if (
        !this.viewerPrefetchScheduled
        && !this.props.loading
        && data
        && data.length > 0
        && shouldEnablePreloading()
      ) {
        scheduleAuthenticatedViewerPrefetch();
        this.viewerPrefetchScheduled = true;
      }

      this.syncAsyncPageLocation(prevProps);
      this.syncAsyncCount(prevProps);
      this.reconcileCurrentRecordIndex(prevProps);
    }

    // ENG-2514: keep `currentRecordIndex` in sync with the visible (post-transform)
    // table rows so slideout viewer Next/Prev never lands on the wrong document
    // after a referral group is expanded/collapsed mid-session. We re-locate the
    // current record by its stable file_token / send_token; if the row is no
    // longer visible (e.g. the user collapsed the group containing it) we close
    // the viewer.
    reconcileCurrentRecordIndex = (prevProps) => {
      const {
        currentRecordToken, currentRecordIndex, newRecordViewerOpen, isNavigationLoading,
      } = this.state;
      // Cross-page Next/Prev awaits a page fetch and then calls navigateToIndex
      // inside a setState callback to point `currentRecordToken`/`currentRecordIndex`
      // at the new page's record. componentDidUpdate fires synchronously between
      // the page-fetch dispatch and the setState callback, so reconciling here
      // would match the still-stale token against the new page's rows, find
      // nothing, and close the viewer mid-flight. The navigation flow itself
      // resets index/token, so we skip reconciliation while it is in progress.
      if (isNavigationLoading) return;
      if (currentRecordToken === null || currentRecordIndex === null) return;

      const tableRows = this.getTableRows();
      const prevTableRows = this.getTableRowsForProps(prevProps);
      // transformRows implementations (e.g. groupReferralRows) memoize on their
      // inputs and return the same reference when nothing relevant changed, so
      // identity equality is a valid no-op signal for both raw-data and
      // transform-backed pages.
      if (tableRows === prevTableRows) return;

      const newIndex = tableRows.findIndex(
        (row) => (row?.file_token || row?.send_token) === currentRecordToken,
      );

      if (newIndex === -1) {
        // The currently viewed record is no longer in the visible row set
        // (group collapsed or row removed). Close the viewer to avoid
        // navigation jumping to an unrelated document.
        if (newRecordViewerOpen) {
          this.setState({
            newRecordViewerOpen: false,
            currentRecordIndex: null,
            currentRecordToken: null,
          });
        } else {
          this.setState({ currentRecordIndex: null, currentRecordToken: null });
        }
        return;
      }

      if (newIndex !== currentRecordIndex) {
        this.setState({ currentRecordIndex: newIndex });
      }
    };

    // Mirror of getTableRows but evaluated against an arbitrary props object so
    // we can compare the previous render's transformed rows to the current ones.
    getTableRowsForProps = (someProps) => {
      const data = someProps?.data;
      if (typeof PassedProps.transformRows === 'function') {
        return PassedProps.transformRows(data, someProps) || data;
      }
      return data;
    };

    componentWillUnmount() {
      // ENG-2465: Clear preload cache on unmount
      clearPreloadCache('unmount');
      this.abortAsyncCountRequest();
      this.cancelSilentRefresh();
    }

    cancelSilentRefresh = () => {
      if (this.silentRefreshTimer) {
        clearTimeout(this.silentRefreshTimer);
        this.silentRefreshTimer = null;
      }
      if (this.silentRefreshAbortController) {
        this.silentRefreshAbortController.abort();
        this.silentRefreshAbortController = null;
      }
      this.silentRefreshPending = false;
    };

    scheduleSilentRefresh = () => {
      if (!Records.SILENT_REFRESH_MODES.includes(PassedProps.mode)) return;

      this.silentRefreshPending = true;
      if (this.silentRefreshTimer) {
        clearTimeout(this.silentRefreshTimer);
      }
      this.silentRefreshTimer = setTimeout(
        this.fireSilentRefresh,
        Records.SILENT_REFRESH_DEBOUNCE_MS,
      );
    };

    fireSilentRefresh = async () => {
      this.silentRefreshTimer = null;
      if (!this.silentRefreshPending) return;

      // Defer while the slideout viewer is open: a background refetch can remove
      // the record the user is looking at, and reconcileCurrentRecordIndex would
      // snap the viewer shut mid-review. componentDidUpdate re-schedules the
      // refresh when the viewer closes.
      if (this.state.newRecordViewerOpen) return;

      // A user-initiated fetch is in flight; stay pending instead of racing
      // it. componentDidUpdate re-schedules the refresh when loading flips
      // false, so even a very slow fetch cannot drop the reconciliation.
      if (this.props.loading) return;

      this.silentRefreshPending = false;

      // Abort a superseded in-flight silent request: both hit the same query,
      // but if the older response landed after the newer one it would dispatch
      // the same FINISHED_* action with rows from before the later mutation.
      if (this.silentRefreshAbortController) {
        this.silentRefreshAbortController.abort();
      }

      const search = this.normalizedQuery(this.props.location.search);
      const abortController = new AbortController();
      this.silentRefreshAbortController = abortController;

      let response;
      try {
        response = await this.props.getPageDataWithQuery(search, abortController.signal, { silent: true });
      } catch {
        // Background refreshes must never surface errors to the table (crud
        // already reports network failures); a rejection here would otherwise
        // be unhandled since this runs from a timer.
        response = null;
      } finally {
        if (this.silentRefreshAbortController === abortController) {
          this.silentRefreshAbortController = null;
        }
      }

      // Row data refreshed; also refresh the async total in the background so
      // pagination doesn't go stale (async counts are cached per query key).
      if (
        response
        && !response.error
        && !response.canceled
        && this.countState() === 'async'
        && this.asyncCountSupportedForQuery(search)
      ) {
        this.asyncCountResolvedKey = null;
        this.fetchAsyncCount(this.asyncCountQueryKeyForQuery(search));
      }
    };

    getQueryData(props) {
      const { location: { search } } = props;
      // Normalize the query string to ensure consistent array format (brackets)
      // This handles cases where the URL might have duplicate params without brackets
      this.getPageDataWithQueryWithAsyncCount(this.resolveInitialSearch(props, search));
    }

    // Teams v3 (D6): let a page inject a sticky value (e.g. the team-view filter)
    // into the very FIRST fetch on mount/navigate-back, so a saved filter is
    // applied without racing an initial team-less fetch that would otherwise
    // clobber it. When the query is rewritten we also replace the URL so the
    // filter bar shows the value and treats it as already-applied (no duplicate
    // fetch). No-op for pages that don't supply resolveInitialQuery.
    resolveInitialSearch = (props, search) => {
      if (typeof PassedProps.resolveInitialQuery !== 'function') return search;
      let nextSearch = search;
      try {
        nextSearch = PassedProps.resolveInitialQuery(search) || search;
      } catch {
        return search;
      }
      if (nextSearch !== search) {
        const { history, location } = props;
        if (history && typeof history.replace === 'function') {
          history.replace(`${location.pathname}${nextSearch}`);
        }
      }
      return nextSearch;
    };

    getPageDataWithQueryWithAsyncCount(query, ...args) {
      // A user-driven fetch supersedes any queued/in-flight background refresh.
      this.cancelSilentRefresh();
      const normalizedSearch = this.normalizedQuery(query);
      const response = this.props.getPageDataWithQuery(normalizedSearch, ...args);
      this.primeAsyncCount(normalizedSearch);
      return response;
    }

    normalizedQuery = (query) => stringify(parse(query || '', parseOptions), stringifyOptions);

    handleChangeDefaultOrganization = async (org) => {
      const {
        changeDefaultOrganization: handleChangeDefaultOrganization,
      } = this.props;
      this.setState({ currentOrganization: org });
      await handleChangeDefaultOrganization(org, this.reloadPage);
    };

    handleOrganizationMenu = async (value) => {
      this.setState({ openOrganizationFilterMenu: value });
    };

    toggleReplySidebar = () => {
      this.setState((prevState) => ({
        viewReplySidebar: !prevState.viewReplySidebar,
      }));
    };

    toggleSmsSidebar = () => {
      this.setState((prevState) => ({
        viewSmsSidebar: !prevState.viewSmsSidebar,
      }));
    };

    toggleReferralReplySidebar = () => {
      this.setState((prevState) => ({
        viewReferralReplySidebar: !prevState.viewReferralReplySidebar,
      }));
    };

    getReplyData = async (uri, sendToken, row = null) => {
      const { getRecordResponseById, markRecordAsViewed } = this.props;
      // Keep the clicked row so the drawer can show the reply-level chart
      // status (ENG-3012) — the presentation payload doesn't carry it.
      this.setState({ replyRow: row });
      await getRecordResponseById(uri);
      this.toggleReplySidebar();
      await markRecordAsViewed(sendToken);
    };

    getSmsData = async (uri) => {
      const { getRecordResponseById } = this.props;
      await getRecordResponseById(uri);
      this.toggleSmsSidebar();
    };

    getReferralReplyData = async (uri) => {
      const { getRecordResponseById } = this.props;
      await getRecordResponseById(uri);
      this.toggleReferralReplySidebar();
    };

    reloadPage = async () => {
      const {
        getLoginInfo: handleGetLoginInfo,
      } = this.props;
      await handleGetLoginInfo();
      window.location.reload();
    };

    getPage = () => {
      const { headers } = this.props;
      return headers ? parseInt(headers.page, 10) : 1;
    };

    getPerPage = () => {
      const { headers } = this.props;
      return headers ? parseInt(headers['per-page'], 10) || 10 : 10;
    };

    hasMore = () => {
      const { headers } = this.props;
      return headers ? headers['has-more'] === 'true' : false;
    };

    countState = () => {
      const { headers } = this.props;
      return headers ? headers['count-state'] || 'exact' : 'exact';
    };

    displayCountState = () => {
      if (this.countState() === 'async' && this.currentAsyncCountMetadata()) {
        return 'exact';
      }

      return this.countState();
    };

    getTotalPages = () => {
      const asyncCountMetadata = this.currentAsyncCountMetadata();
      if (asyncCountMetadata) {
        return asyncCountMetadata.total_pages;
      }

      if (this.countState() === 'async') {
        return this.getPage() + (this.hasMore() ? 1 : 0);
      }

      const { headers } = this.props;
      return headers ? parseInt(headers['total-pages'], 10) : 1;
    };

    getTotalResults = (rowsLength = this.props.data.length) => {
      const asyncCountMetadata = this.currentAsyncCountMetadata();
      if (asyncCountMetadata) {
        return asyncCountMetadata.total_count;
      }

      if (this.countState() === 'async') {
        return ((this.getPage() - 1) * this.getPerPage()) + rowsLength + (this.hasMore() ? 1 : 0);
      }

      const { headers } = this.props;
      return headers ? parseInt(headers.total, 10) : 0;
    };

    syntheticAsyncCountMetadata = (rowsLength = this.props.data.length) => ({
      total_count: ((this.getPage() - 1) * this.getPerPage()) + rowsLength + (this.hasMore() ? 1 : 0),
      total_pages: this.getPage() + (this.hasMore() ? 1 : 0),
    });

    currentQueryParams = (props = this.props) => {
      const params = parse(props.location.search, parseOptions);

      if (PassedProps.mode === 'Referred') {
        params.referrals = 'true';
        params.category = 'Referral';
      }

      return params;
    };

    applyReferralDefaults = (params) => {
      if (PassedProps.mode === 'Referred') {
        params.referrals = 'true';
        params.category = 'Referral';
      }
      return params;
    };

    asyncCountQueryKeyFromParams = (params) => {
      const nextParams = { ...params };
      this.applyReferralDefaults(nextParams);
      delete nextParams.page;
      delete nextParams.sort;
      delete nextParams.sort_dir;

      return stringify(nextParams, stringifyOptions);
    };

    asyncCountQueryKeyForQuery = (query) => {
      const params = parse(query || '', parseOptions);
      return this.asyncCountQueryKeyFromParams(params);
    };

    asyncCountQueryKey = (props = this.props) => {
      const headers = props.headers || {};
      if ((headers['count-state'] || 'exact') !== 'async') return null;

      const params = this.currentQueryParams(props);
      return this.asyncCountQueryKeyFromParams(params);
    };

    activeAsyncCountQueryKey = () => {
      const responseKey = this.asyncCountQueryKey();
      if (responseKey !== null) return responseKey;

      const { location } = this.props;
      if (!this.asyncCountSupportedForQuery(location.search)) return null;

      return this.asyncCountQueryKeyForQuery(location.search);
    };

    currentAsyncCountMetadata = () => {
      if (this.countState() !== 'async') return null;

      const currentKey = this.activeAsyncCountQueryKey();
      if (currentKey === null || this.state.asyncCountMetadataKey !== currentKey) return null;

      return this.state.asyncCountMetadata;
    };

    asyncCountSupportedForQuery = () => ['Received', 'Referred'].includes(PassedProps.mode);

    primeAsyncCount = (query) => {
      if (!this.asyncCountSupportedForQuery(query)) return;

      const currentKey = this.asyncCountQueryKeyForQuery(query);
      if (this.asyncCountResolvedKey === currentKey || this.asyncCountPendingKey === currentKey) return;

      this.resetAsyncCount();
      this.fetchAsyncCount(currentKey);
    };

    abortAsyncCountRequest = () => {
      if (this.asyncCountRetryTimeout) {
        clearTimeout(this.asyncCountRetryTimeout);
        this.asyncCountRetryTimeout = null;
      }
      if (this.asyncCountAbortController) {
        this.asyncCountAbortController.abort();
        this.asyncCountAbortController = null;
      }
      this.asyncCountPendingKey = null;
    };

    resetAsyncCount = () => {
      this.abortAsyncCountRequest();
      this.asyncCountResolvedKey = null;
      if (this.state.asyncCountMetadata) {
        this.setState({ asyncCountMetadata: null, asyncCountMetadataKey: null });
      }
    };

    syncAsyncCount = (prevProps) => {
      const currentKey = this.asyncCountQueryKey(this.props);
      const previousKey = this.asyncCountQueryKey(prevProps);

      if (currentKey !== previousKey) {
        if (!(currentKey !== null && (
          this.asyncCountResolvedKey === currentKey ||
          this.asyncCountPendingKey === currentKey
        ))) {
          this.resetAsyncCount();
        }
      }

      if (currentKey === null) return;
      if (this.asyncCountResolvedKey === currentKey || this.asyncCountPendingKey === currentKey) return;

      this.fetchAsyncCount(currentKey);
    };

    syncAsyncPageLocation = (prevProps) => {
      if (this.countState() !== 'async') return;

      const params = this.currentQueryParams();
      const currentPage = this.getPage();
      const requestedPage = parseInt(params.page, 10) || 1;
      if (requestedPage === currentPage) return;

      const { history, location } = this.props;
      if (!history?.replace) return;

      params.page = currentPage.toString();
      history.replace({
        ...location,
        search: stringify(params, stringifyOptions),
      });
    };

    fetchAsyncCount = async (queryKey, attempt = 0) => {
      if (attempt === 0) {
        this.abortAsyncCountRequest();
      }

      const abortController = new AbortController();
      this.asyncCountAbortController = abortController;
      this.asyncCountPendingKey = queryKey;

      try {
        const response = await axios.get(`/api/v1/receivers/count${queryKey}`, {
          signal: abortController.signal,
        });

        if (this.asyncCountPendingKey !== queryKey) return;
        if (this.activeAsyncCountQueryKey() !== queryKey) {
          this.asyncCountPendingKey = null;
          this.asyncCountAbortController = null;
          return;
        }

        this.asyncCountResolvedKey = queryKey;
        this.asyncCountPendingKey = null;
        this.asyncCountAbortController = null;
        this.setState({ asyncCountMetadata: response.data, asyncCountMetadataKey: queryKey });
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || axios.isCancel?.(error)) {
          return;
        }

        if (this.asyncCountPendingKey !== queryKey) {
          return;
        }

        this.asyncCountAbortController = null;

        if (attempt < 1) {
          this.asyncCountRetryTimeout = setTimeout(() => {
            this.asyncCountRetryTimeout = null;
            this.fetchAsyncCount(queryKey, attempt + 1);
          }, this.asyncCountRetryDelay());
          return;
        }

        this.asyncCountPendingKey = null;
      }
    };

    asyncCountRetryDelay = () => {
      const baseDelay = Records.ASYNC_COUNT_RETRY_DELAY_MS;
      return baseDelay + Math.floor(Math.random() * baseDelay);
    };

    // Check if a record is a reply row (has uri that should open sidebar instead of viewer)
    isReplyRow = (record) => {
      return record && !!record.uri;
    };

    // Find the next valid (non-reply) record index in a given direction
    findNextValidIndex = (startIndex, direction, data = this.getTableRows()) => {
      if (!data || !data.length) return -1;

      let index = startIndex;
      while (index >= 0 && index < data.length) {
        const record = data[index];
        if (!this.isReplyRow(record)) {
          return index;
        }
        index += direction;
      }
      return -1; // No valid record found
    };

    navigateToIndex = (index, data = this.getTableRows()) => {
      if (!data || !data.length) return;
      if (index >= 0 && index < data.length) {
        const record = data[index];

        // Skip reply rows - they should open the sidebar, not the viewer
        // This mirrors the behavior in InboxTable's row click handler
        if (this.isReplyRow(record)) {
          return;
        }

        this.props.setSelectedRecordDetails(record);
        // ENG-2514: track the record's stable identifier alongside its position
        // so we can re-derive the index after a referral group is expanded /
        // collapsed mid-session.
        this.setState({
          currentRecordIndex: index,
          currentRecordToken: record?.file_token || record?.send_token || null,
        });

        // Mark record as viewed if applicable (same logic as row click in InboxTable)
        if (PassedProps.markRecordAsReceived && !record.viewed) {
          const sendToken = record.send_token;
          if (sendToken) {
            this.props.markRecordAsViewed(sendToken);
          }
        }
      }
    };

    buildSearchWithPage = (currentSearch, newPage) => {
      const params = parse(currentSearch, parseOptions);
      params.page = newPage;
      return stringify(params, stringifyOptions);
    };

    handleNavigatePrevious = async () => {
      // Close EHR drawer before navigating to prevent submitting to wrong record
      if (this.state.ehrDrawerVisible) {
        this.handleCloseEhrDrawer();
      }

      const { location } = this.props;
      const { currentRecordIndex } = this.state;
      const data = this.getTableRows();

      if (currentRecordIndex === null) return;

      const currentPage = this.getPage();

      // Find the previous valid (non-reply) record on the current page
      const prevValidIndex = this.findNextValidIndex(currentRecordIndex - 1, -1, data);

      if (prevValidIndex !== -1) {
        this.navigateToIndex(prevValidIndex, data);
      } else if (currentPage > 1) {
        // No valid records before current index on this page, try previous page
        this.setState({ isNavigationLoading: true });
        const prevPage = currentPage - 1;
        const newSearch = this.buildSearchWithPage(location.search, prevPage);
        try {
          await this.getPageDataWithQueryWithAsyncCount(newSearch);
          // Use setState callback to ensure we have the latest props after Redux update
          this.setState({ isNavigationLoading: false }, () => {
            // Run cross-page navigation against the same post-transform row set
            // the table renders. On referral pages, raw `props.data` may include
            // collapsed linked children that are stripped by transformRows; using
            // those rows directly would let the viewer land on a hidden record.
            const newData = this.getTableRows();
            if (newData && newData.length > 0) {
              // Find the last valid record on the previous page (search backwards from end)
              const validIndex = this.findNextValidIndex(newData.length - 1, -1, newData);
              if (validIndex !== -1) {
                this.navigateToIndex(validIndex, newData);
              }
            }
          });
        } catch (error) {
          console.error('Failed to fetch previous page:', error);
          this.setState({ isNavigationLoading: false });
        }
      }
    };

    handleNavigateNext = async () => {
      // Close EHR drawer before navigating to prevent submitting to wrong record
      if (this.state.ehrDrawerVisible) {
        this.handleCloseEhrDrawer();
      }

      const { location } = this.props;
      const { currentRecordIndex } = this.state;
      const data = this.getTableRows();

      if (currentRecordIndex === null || !data || !data.length) return;

      const currentPage = this.getPage();
      const totalPages = this.getTotalPages();
      const hasMore = this.hasMore();

      // Find the next valid (non-reply) record on the current page
      const nextValidIndex = this.findNextValidIndex(currentRecordIndex + 1, 1, data);

      if (nextValidIndex !== -1) {
        this.navigateToIndex(nextValidIndex, data);
      } else if (hasMore || currentPage < totalPages) {
        // No valid records after current index on this page, try next page
        this.setState({ isNavigationLoading: true });
        const nextPage = currentPage + 1;
        const newSearch = this.buildSearchWithPage(location.search, nextPage);
        try {
          await this.getPageDataWithQueryWithAsyncCount(newSearch);
          // Use setState callback to ensure we have the latest props after Redux update
          this.setState({ isNavigationLoading: false }, () => {
            // Run cross-page navigation against the same post-transform row set
            // the table renders. On referral pages, raw `props.data` may include
            // collapsed linked children that are stripped by transformRows; using
            // those rows directly would let the viewer land on a hidden record.
            const newData = this.getTableRows();
            if (newData && newData.length > 0) {
              // Find the first valid record on the next page (search forward from start)
              const validIndex = this.findNextValidIndex(0, 1, newData);
              if (validIndex !== -1) {
                this.navigateToIndex(validIndex, newData);
              }
            }
          });
        } catch (error) {
          console.error('Failed to fetch next page:', error);
          this.setState({ isNavigationLoading: false });
        }
      }
    };

    isNavigationDisabled = (direction) => {
      const data = this.getTableRows();
      const { currentRecordIndex, isNavigationLoading } = this.state;

      if (currentRecordIndex === null || isNavigationLoading || !data || !data.length) return true;

      const currentPage = this.getPage();
      const totalPages = this.getTotalPages();
      const hasMore = this.hasMore();

      if (direction === 'previous') {
        // Check if there's a valid (non-reply) record before the current index
        const prevValidIndex = this.findNextValidIndex(currentRecordIndex - 1, -1, data);
        const hasValidPrevOnPage = prevValidIndex !== -1;
        const hasPrevPage = currentPage > 1;
        return !hasValidPrevOnPage && !hasPrevPage;
      }
      if (direction === 'next') {
        // Check if there's a valid (non-reply) record after the current index
        const nextValidIndex = this.findNextValidIndex(currentRecordIndex + 1, 1, data);
        const hasValidNextOnPage = nextValidIndex !== -1;
        const hasNextPage = hasMore || currentPage < totalPages;
        return !hasValidNextOnPage && !hasNextPage;
      }
      return false;
    };

    render() {
      const {
        history, location, departments,
      } = this.props;
      const {
        data,
        selectedData,
        rowSelected,
        docked,
        sidebar,
        error,
        loading,
        currentRecordLoading,
        errorMessage,
        errorCode,
        retrying,
        retryAttemptNumber,
        replyData,
        smsData,
        referralReplyData,
        getAttachmentUrl,
        attachmentUrl,
        getLabelsByQuery,
        labels,
        organizationDetails,
      } = this.props;
      const totalPages = this.getTotalPages();
      const hasMore = this.hasMore();
      const countState = this.displayCountState();
      const totalResults = this.getTotalResults(data.length);
      const tableRows = this.getTableRows();
      const tableColumnTitles = this.getTableColumnTitles();
      const onMobile = window.innerWidth < 800;
      const getPageDataWithQuery = this.getPageDataWithQueryWithAsyncCount;

      let page = 1;
      let params = this.currentQueryParams();
      if (location.search || PassedProps.mode === 'Referred') {
        const requestedPage = parseInt(params.page, 10) || 1;
        page = countState === 'async' ? this.getPage() : parseInt((requestedPage <= totalPages ? requestedPage : 1), 10);
      }
      const paramTools = {
        params,
        stringify,
        stringifyOptions,
        push: history.push,
      };

      const filterData = {
        departments,
      };

      const {
        sidebarActions, inboxActions, mode, viewReplySidebar, viewSmsSidebar, viewReferralReplySidebar, currentOrganization, openOrganizationFilterMenu,
      } = this.state;

      const setNewRecordViewerOpen = (index = null) => {
        // ENG-2514: capture the current row's stable identifier (file_token /
        // send_token) so that subsequent referral expand/collapse can reconcile
        // the position-based currentRecordIndex back to the same record.
        const record = (index !== null && tableRows && tableRows[index]) || null;
        const recordToken = record?.file_token || record?.send_token || null;
        this.setState({
          newRecordViewerOpen: true,
          currentRecordIndex: index,
          currentRecordToken: recordToken,
        });
      };

      const closeNewRecordViewer = async () => {
        if (this.props.currentRecordLoading) return;

        this.props.setViewerChatVisible(false);
        await this.props.clearCurrentRecordId();
        await this.props.clearSelectedRecordDetails();
        await this.props.clearDownloadUrl();
        await this.props.clearDocumentComments();
        this.setState({
          newRecordViewerOpen: false,
          currentRecordIndex: null,
          currentRecordToken: null,
        });
      };

      const hideSidebar = PassedProps.hideSidebar;
      const recordViewerClassName = `record-viewer${mode === 'Referred' ? ' record-viewer--referred' : ''}`;

      return (
        <div>
          {!hideSidebar && (
            <Sidebar
              className={PassedProps.sidebarClassName}
              page="inbox"
              docked={docked}
              sidebar={sidebar}
              actions={sidebarActions}
              inboxActions={inboxActions}
              paramTools={paramTools}
              mode={mode}
              getPageDataWithQuery={getPageDataWithQuery}
              filterData={filterData}
              location={location}
              getLabelsByQuery={getLabelsByQuery}
              data={data}
              labels={labels}
            />
          )}
          <div
            style={!hideSidebar && docked ? { marginLeft: '256px' } : { marginLeft: '0' }}
            className={recordViewerClassName}
          >
            { /* The PassedComponent is the parent that plugs into the HOC */ }
            <PassedComponent
              location={location}
              error={error}
              errorCode={errorCode}
              currentOrganization={currentOrganization}
              openOrganizationFilterMenu={openOrganizationFilterMenu}
              handleOrganizationMenu={this.handleOrganizationMenu}
              organizationDetails={organizationDetails}
              changeDefaultOrganization={this.handleChangeDefaultOrganization}
              onOpenEhrDrawer={this.handleOpenEhrDrawer}
              paramTools={paramTools}
              getPageDataWithQuery={getPageDataWithQuery}
              expandedReferralEntitySlugs={this.props.expandedReferralEntitySlugs}
              onToggleReferralEntity={this.props.onToggleReferralEntity}
              teamsEnabled={this.props.teamsEnabled}
              teams={this.props.teams}
              onSetRecordTeamBulk={this.props.onSetRecordTeamBulk}
            />
            {
              // Show page-level loading spinner only when the slideout viewer is NOT open.
              // The viewer's single-record fetch shares isLoading, so we must ignore it here
              // to avoid hiding the already-loaded table when the viewer is fetching.
              loading && !this.state.newRecordViewerOpen && <PageLoading />
            }
            {
              loading && retrying && !this.state.newRecordViewerOpen && (
                <div style={{ marginTop: '10px', textAlign: 'center', fontSize: '20px' }}>
                  Something went wrong, we are automatically retrying...
                  { retryAttemptNumber > 0 && (
                  <p>
                    Retry attempt #
                    {retryAttemptNumber}
                  </p>
                  )}
                </div>
              )
            }
            {
              // Loading has stopped but there's an error, display it in the
              // center of the page. Skip when viewer is open — viewer errors
              // are shown inside the viewer, not on the main page.
            !loading && errorMessage && !this.state.newRecordViewerOpen && <ErrorDisplay message={errorMessage} error={errorCode} />
            }
            {
              // Not loading and no error message, so it is safe to
              // render the records in the table.
              // When the viewer is open, always keep the table visible — the data
              // was already loaded (user clicked a row to open the viewer).
              ((!errorMessage && !loading) || this.state.newRecordViewerOpen)
              && (
                <InboxTable
                  rows={tableRows}
                  actions={inboxActions}
                  push={history.push}
                  page={page}
                  totalPages={totalPages}
                  totalResults={totalResults}
                  countState={countState}
                  hasMore={hasMore}
                  selectedData={selectedData}
                  paramTools={paramTools}
                  rowSelected={rowSelected}
                  columnTitles={tableColumnTitles}
                  enableSelectAll={PassedProps.enableSelectAll}
                  syncSelectionFromStore={PassedProps.syncSelectionFromStore}
                  renderSelectionCell={PassedProps.renderSelectionCell}
                  mode={mode}
                  getReplyData={this.getReplyData}
                  getSmsData={this.getSmsData}
                  getReferralReplyData={this.getReferralReplyData}
                  setNewRecordViewerOpen={setNewRecordViewerOpen}
                  markRecordAsReceived={PassedProps.markRecordAsReceived}
                  disableRowSelection={PassedProps.disableRowSelection}
                />
              )
            }
            {
              viewReplySidebar && (
                <ReplyMessageSidebar
                  error={error}
                  toggleReplySidebar={this.toggleReplySidebar}
                  data={replyData}
                  row={this.state.replyRow}
                  getAttachmentUrl={getAttachmentUrl}
                  attachmentUrl={attachmentUrl}
                />
              )
            }
            {
              viewSmsSidebar && (
                <SmsMessageSidebar
                  error={error}
                  toggleSmsSidebar={this.toggleSmsSidebar}
                  data={smsData}
                />
              )
            }
            {
              viewReferralReplySidebar && (
                <ReferralReplySidebar
                  error={error}
                  toggleReferralReplySidebar={this.toggleReferralReplySidebar}
                  data={referralReplyData}
                  getReferralReplies={getRecordResponseById}
                />
              )
            }
          </div>
          {!onMobile && this.state.newRecordViewerOpen && (
            <ViewerLoadBoundary
              resetKey={`${this.state.newRecordViewerOpen}-${selectedData?.[0]?.file_token || ''}`}
            >
              <React.Suspense fallback={<PageLoading />}>
                <NewRecordViewer
                  open={this.state.newRecordViewerOpen}
                  closeViewer={closeNewRecordViewer}
                  onNavigatePrevious={this.handleNavigatePrevious}
                  onNavigateNext={this.handleNavigateNext}
                  disablePrevious={this.isNavigationDisabled('previous')}
                  disableNext={this.isNavigationDisabled('next')}
                  isNavigationLoading={this.state.isNavigationLoading}
                  isRecordLoading={currentRecordLoading}
                  onOpenEhrDrawer={this.handleOpenEhrDrawer}
                />
              </React.Suspense>
            </ViewerLoadBoundary>
          )}
          <DynamicEhrDrawer
            visible={this.state.ehrDrawerVisible}
            drawerData={this.state.ehrDrawerData}
            onClose={this.handleCloseEhrDrawer}
          />
        </div>
      );
    }
  }

  Records.propTypes = {
    clearCurrentRecordId: PropTypes.func.isRequired,
    clearSelectedRecordDetails: PropTypes.func.isRequired,
    clearDownloadUrl: PropTypes.func.isRequired,
    clearDocumentComments: PropTypes.func.isRequired,
  };

  return connect((state) => {
    const currentRecordRequest = state.records.currentRecordRequest || {};

    return {
      columnTitles: PassedProps.columnTitles,
      error: state.records.error,
      data: state.records[PassedProps.data],
      docked: state.appState.sidebarVisible,
      departments: state.auth.departments,
      errorMessage: state.records.errorMessage,
      currentRecordLoading: currentRecordRequest.loading,
      header: PassedProps.header,
      headers: state.records.headers,
      loading: state.records.isLoading,
      retrying: state.records.retrying,
      retryAttemptNumber: state.records.retryAttemptNumber,
      locationPath: PassedProps.locationPath,
      selectedData: state.inboxTable.recordData,
      sendData: state.inboxTable.sendData,
      sidebar: state.sidebar,
      errorCode: state.records.errorCode,
      replyData: state.replies.data.reply,
      smsData: state.replies.data,
      referralReplyData: state.replies.data,
      attachmentUrl: state.replies.attachmentUrl,
      labels: state.labels.data,
      organizationDetails: state.auth.organizationDetails,
      inboxRefreshNonce: state.records.inboxRefreshNonce,
    };
  }, {
    checkTriggered,
    changeSidebarDate,
    changeSidebarMenu,
    rowSelected,
    changeInboxPage,
    getRecordResponseById,
    getAttachmentUrl,
    markRecordAsViewed,
    getSelectedPageData: PassedProps.getSelectedPageData,
    getPageDataWithQuery: PassedProps.getPageDataWithQuery,
    revokeToggle: PassedProps.revokeToggle,
    getLabelsByQuery,
    changeDefaultOrganization,
    getLoginInfo,
    setViewerChatVisible,
    clearCurrentRecordId,
    clearSelectedRecordDetails,
    clearDownloadUrl,
    clearDocumentComments,
    setSelectedRecordDetails,
  })(Records);
};

export default RecordsHOC;
